import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { ReactElement, Ref, forwardRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import UploadIcon from 'assets/icons/UploadIcon';
import { analytics, events } from 'services/analytics';
import { AdvancedBundle } from 'services/store/ai/types';
import { formulaToRecord } from '../../helpers/formula';
import { StyledBundleActionButton } from '../BundleContainer/styled';
import { useFormulaUrl } from './useFormulaUrl';

const Transition = (
  props: TransitionProps & {
    children: ReactElement;
  },
  ref: Ref<unknown>
) => <Slide direction="up" ref={ref} {...props} />;

const TransitionRef = forwardRef(Transition);

interface ShareFormulaButtonProps {
  bundle: AdvancedBundle;
}

const ShareFormulaButton = ({ bundle }: ShareFormulaButtonProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const { genre, formula } = bundle;
  const formulaUrl = useFormulaUrl(bundle);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={t('aiMusicGenerator:shareFormula:tooltipTitle')}>
        <StyledBundleActionButton
          onClick={() => {
            setOpen(true);
            analytics.track(
              events.shareFormulaClick(genre, formulaToRecord(formula))
            );
          }}
        >
          <UploadIcon />
        </StyledBundleActionButton>
      </Tooltip>
      <Dialog
        PaperProps={{
          sx: {
            padding: theme.spacing(7, 8),
            backgroundColor: theme.palette.grey[50],
            color: theme.palette.grey[900],
          },
        }}
        open={open}
        TransitionComponent={TransitionRef}
        keepMounted
        onClose={handleClose}
      >
        <Box
          sx={{
            position: 'absolute',
            top: theme.spacing(2),
            right: theme.spacing(2),
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              padding: theme.spacing(0.25),
              color: 'inherit',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: 24,
              marginBottom: theme.spacing(2),
            }}
          >
            {t('aiMusicGenerator:shareFormula:heading')}
          </Typography>
          <Typography
            sx={{
              marginBottom: theme.spacing(1),
            }}
          >
            {t('aiMusicGenerator:shareFormula:description')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: theme.spacing(1),
            }}
          >
            <TextField
              value={formulaUrl}
              inputProps={{
                readOnly: true,
                onFocus: ({ target }) => target.select(),
              }}
              sx={{
                background: theme.palette.grey[50],
                borderRadius: theme.spacing(0.5),
                '.MuiOutlinedInput-root': {
                  height: theme.spacing(4.5),
                  color: theme.palette.grey[600],
                },
                '.MuiInputBase-input': {
                  boxSizing: 'border-box',
                  height: '100%',
                  padding: theme.spacing(0, 1),
                  fontSize: 12,
                  width: theme.spacing(42),
                },
              }}
            />
            {/* @ts-ignore */}
            <CopyToClipboard
              text={formulaUrl}
              onCopy={() => {
                setIsCopied(true);
                analytics.track(
                  events.copyFormulaUrl(genre, formulaToRecord(formula))
                );
              }}
            >
              <Button variant="rounded">
                {isCopied
                  ? t('aiMusicGenerator:shareFormula:copyButton:titleCopied')
                  : t('aiMusicGenerator:shareFormula:copyButton:title')}
              </Button>
            </CopyToClipboard>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareFormulaButton;
